import type { LocationData } from '@/types/location.types';

/* ============================================================
   THE CLUB THE MEMBER IS BOOKING AT

   Which club a booking flow is pointed at used to live in router
   state and nowhere else. That works while every step passes it
   along, and breaks the moment one does not: a member picks a
   secondary club on Book, opens a session type, comes back — and
   the screen, finding no `selectedLocation` on this history entry,
   falls back to their home club. Their choice is silently undone
   one step before they commit to it, which is the worst possible
   place to lose it.

   So the choice outlives the route. It is remembered only when the
   member actually picks a club; the home club stays the default and
   is never written, because falling back to it is not a decision.

   SESSION STORAGE, NOT LOCAL. A club chosen for one visit should
   not still be chosen next week — a drop-in is an exception, and
   an exception that persists forever becomes a trap. Closing the
   app ends it. Navigating away from the booking flow ends it
   sooner: `TabBar` forgets on every tab, so Home, My Game, Shop and
   Profile all put the member back on their own club.

   Every access is guarded. Storage throws in a WebView with site
   data disabled, and a booking flow must not die because a
   preference could not be cached.
   ============================================================ */

const KEY = 'tb.booking-location';

/** Remember an explicit club choice. */
export const rememberBookingLocation = (location: LocationData): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(location));
  } catch {
    /* No storage — the flow still works, the choice just does not
       survive a screen that arrives without router state. */
  }
};

/** The club chosen earlier in this visit, if there was one. */
export const recallBookingLocation = (): LocationData | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocationData;
    /* An id is the whole point of the record — anything without one
       cannot be used to fetch booking types, so treat it as absent
       rather than handing a half-object to the screen. */
    return parsed?.id ? parsed : null;
  } catch {
    return null;
  }
};

/** Back to the member's own club. */
export const forgetBookingLocation = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* Nothing was stored either. */
  }
};
